import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import api from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import FilterBar from '../../components/common/FilterBar';
import { cldImg } from '../../utils/cloudinary';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { forceTopOnReturn } from '../../utils/scrollMemory';

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-square bg-gray-100" />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-5 bg-gray-100 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Page lives in the URL (?page=N) so that paginating pushes a history entry:
  // clicking page 2 scrolls to top, and Back from page 2 returns to page 1
  // instead of jumping to wherever the user came from.
  const page = parseInt(searchParams.get('page') || '1');
  // Hydrate from cache on Back/Forward for instant paint + seamless scroll restore.
  const cached = getPageCache(`category:${slug}`);
  const [category, setCategory] = useState(cached?.category || null);
  const [products, setProducts] = useState(cached?.products || []);
  const [loading, setLoading] = useState(!cached);
  const [totalPages, setTotalPages] = useState(cached?.totalPages || 1);
  const [total, setTotal] = useState(cached?.total || 0);
  const [sort, setSort] = useState('createdAt-DESC');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(99999);
  const [categoryBanner, setCategoryBanner] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const catRes = await api.get('/categories');
      const cats = catRes.data.categories || [];
      const cat = cats.find(c => c.slug === slug);
      if (!cat) { setLoading(false); return; }
      setCategory(cat);

      const [sortField, sortOrder] = sort.split('-');
      const params = { category: cat.id, page, limit: 20, sort: sortField, order: sortOrder };
      if (priceMin > 0) params.priceMin = priceMin;
      if (priceMax < 99999) params.priceMax = priceMax;

      const { data } = await api.get('/products', { params });
      setProducts(data.products || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
      // Cache the default view (no filter/pagination) so Back restores instantly.
      if (page === 1 && priceMin === 0 && priceMax === 99999) {
        setPageCache(`category:${slug}`, { category: cat, products: data.products || [], totalPages: data.pages || 1, total: data.total || 0 });
      }
    } catch {}
    finally { setLoading(false); }
  }, [slug, page, sort, priceMin, priceMax]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 (drop ?page) whenever the category or a filter changes.
  const resetPage = useCallback(() => {
    const n = new URLSearchParams(searchParams);
    if (n.has('page')) { n.delete('page'); setSearchParams(n, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const goToPage = (p) => {
    // Coming Back to this entry should land on the header, not where we scrolled
    // down to reach the pagination controls.
    forceTopOnReturn(location.key);
    const n = new URLSearchParams(searchParams);
    if (p > 1) n.set('page', p); else n.delete('page');
    setSearchParams(n);
    window.scrollTo(0, 0);
  };

  const handleSortChange = (v) => { setSort(v); resetPage(); };

  useEffect(() => {
    api.get(`/banners?type=category&category_slug=${slug}`)
      .then(r => setCategoryBanner(r.data.banners?.[0] || null))
      .catch(() => {});
  }, [slug]);

  const handlePriceChange = (min, max) => {
    setPriceMin(min);
    setPriceMax(max);
    resetPage();
  };

  if (!loading && !category) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center">
        <p className="text-gray-400 text-lg">Category not found</p>
        <Link to="/products" className="btn-primary px-6 py-3 rounded-xl mt-4 inline-block">Browse All Products</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
        <Link to="/" className="hover:text-brand-yellow">Home</Link>
        <span>›</span>
        <span className="text-brand-dark font-medium">{category?.name || slug}</span>
      </div>

      {/* Category banner */}
      {categoryBanner && (
        <div className="mb-5 rounded-2xl overflow-hidden">
          <img src={cldImg(categoryBanner.image_url, 1200)} alt={categoryBanner.title || category?.name}
            fetchpriority="high" decoding="async" className="w-full h-auto" />
        </div>
      )}

      {/* Category title */}
      <h1 className="text-xl font-bold text-brand-dark mb-4">{category?.name || slug}</h1>

      {/* Horizontal filter bar */}
      <FilterBar
        sortValue={sort}
        onSortChange={handleSortChange}
        priceMin={priceMin}
        priceMax={priceMax}
        onPriceChange={handlePriceChange}
        total={total}
        loading={loading}
      />

      {/* Products grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
        {loading && products.length === 0
          ? [1,2,3,4,5,6,7,8,9,10].map(i => <SkeletonCard key={i} />)
          : products.length === 0
            ? (
              <div className="col-span-full text-center py-20">
                <p className="text-5xl mb-4">🛍️</p>
                <p className="text-gray-400">No products in this category yet</p>
                <Link to="/products" className="btn-primary px-6 py-3 rounded-xl mt-4 inline-block">Browse All</Link>
              </div>
            )
            : products.map(p => <ProductCard key={p.id} product={p} />)
        }
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-10">
          <button disabled={page === 1} onClick={() => goToPage(page - 1)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium disabled:opacity-40 hover:border-brand-dark transition-colors">
            ← Prev
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const p = Math.max(1, page - 2) + i;
            if (p > totalPages) return null;
            return (
              <button key={p} onClick={() => goToPage(p)}
                className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${page === p ? 'bg-brand-yellow text-brand-dark' : 'border border-gray-200 text-gray-600 hover:border-brand-dark'}`}>
                {p}
              </button>
            );
          })}
          <button disabled={page === totalPages} onClick={() => goToPage(page + 1)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium disabled:opacity-40 hover:border-brand-dark transition-colors">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
